import type { LogLevel, LogContext, Logger } from '../types/entityConfig'

export const LEVEL_PRIORITY: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
}

const LEVEL_LABELS: Record<LogLevel, string> = {
  debug: '[DEBUG]',
  info: '[INFO] ',
  warn: '[WARN] ',
  error: '[ERROR]',
}

function getLogLevel(): LogLevel {
  const env = import.meta.env
  return (env.VITE_LOG_LEVEL as LogLevel) ?? (env.DEV ? 'debug' : 'warn')
}

export function serializeContext(context: LogContext): string {
  const json = JSON.stringify(context)
  if (json.length > 500) {
    return json.slice(0, 497) + '...'
  }
  return json
}

export function formatMessage(
  level: LogLevel,
  module: string,
  message: string,
  context?: LogContext,
): string {
  const base = `${LEVEL_LABELS[level]} [${module}] ${message}`
  if (context && Object.keys(context).length > 0) {
    return `${base} ${serializeContext(context)}`
  }
  return base
}

export function useLogger(module: string): Logger {
  const threshold = LEVEL_PRIORITY[getLogLevel()]

  function log(level: LogLevel, message: string, context?: LogContext): void {
    if (LEVEL_PRIORITY[level] < threshold) return
    const formatted = formatMessage(level, module, message, context)
    console[level](formatted)
  }

  return {
    debug: (message: string, context?: LogContext) => log('debug', message, context),
    info: (message: string, context?: LogContext) => log('info', message, context),
    warn: (message: string, context?: LogContext) => log('warn', message, context),
    error: (message: string, context?: LogContext) => log('error', message, context),
  }
}
